import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Text } from '@famly/mf_data-display_text';
import { Stack, createStack } from '@famly/mf_layout_stack';
import { useCustomWhitelabelColor } from 'signin-app/components/hooks/use-custom-whitelabel-color';
// Image used as a placeholder for groups, tags and institutions by the backend
const EMPTY_IMAGE_NAME = 'empty-group.jpg';
const StyledItemContainer = styled(createStack({
    width: '100%',
    px: { base: 4, tabletPortrait: 6 },
    py: { base: 3, tabletPortrait: 4 },
    gap: 2,
})).withConfig({
    shouldForwardProp: prop => prop !== 'selected' && prop !== 'customP200' && prop !== 'customP300' && prop !== 'alignment',
}) `
    box-sizing: border-box;
    background-color: ${props => props.theme.mf.colorPalette.n0};
    border: 1px solid ${props => props.theme.mf.colorPalette.n200};
    border-radius: ${props => props.theme.mf.spacing(4)};
    position: relative;
    cursor: pointer;
    align-items: ${props => (props.alignment === 'vertical' ? 'flex-start' : 'center')};

    ${props => props.selected
    ? css `
                  border-color: ${props.customP300};
                  background-color: ${props.customP200};
              `
    : ''}
`;
const ImageContainer = styled(createStack({
    alignItems: 'center',
    justifyContent: 'center',
})) `
    width: ${props => (props.size === 'large' ? '72px' : '48px')};
    height: ${props => (props.size === 'large' ? '72px' : '48px')};
    border-radius: 50%;
    border: 1px solid white;
    box-sizing: border-box;
    flex-shrink: 0;
    font-size: 20px;
    ${props => props.color
    ? css `
                  background: ${props.color};
              `
    : ''}
`;
const StyledTextContainer = styled(createStack({})) `
    max-width: 150px;
    * {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;
const StyledImage = styled.img `
    width: 100%;
    border-radius: 50%;
`;
const StyledCount = styled(createStack({
    position: 'absolute',
    top: '0',
    right: '0',
    width: '24px',
    height: '24px',
    backgroundColor: 'b400',
    color: 'n0',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
})) `
    transform: translate(9px, -9px);
    overflow: hidden;
`;
const StyledLink = styled(Link) `
    width: 100%;
`;
export const CardsContainer = styled(createStack({
    display: 'grid',
    gap: '16px',
    justifyContent: 'space-between',
})).withConfig({
    shouldForwardProp: prop => prop !== 'isCompact',
}) `
    grid-template-columns: repeat(auto-fill, minmax(${props => (props.isCompact ? '200px' : '320px')}, 1fr));
`;
export const GroupItem = ({ image, imageSize, title, href, checkedInCount, className, groupType, groupId, alignment, dataE2eCardContainerClass, }) => {
    const displayTitle = React.useMemo(() => {
        if (groupType === 'Children') {
            return title;
        }
        // If the number of employees is over a limit set by the backend,
        // we get multiple staff groups with names including a range of employee initials
        // in the given group, e.g. `Staff A-W`, `Staff W-Z` instead of just `Staff`.
        const [staffTitle, initials] = title.split(' ');
        return initials ? initials : staffTitle;
    }, [groupType, title]);
    return (_jsx(Stack, { children: _jsx(StyledLink, { to: href, className: className, "data-e2e-class": "signin-group-card", children: _jsxs(Stack, { position: "relative", width: "100%", children: [_jsx(Card, { image: image, imageSize: imageSize, displayTitle: displayTitle, color: getGroupColor(groupId), alignment: alignment, dataE2eCardContainerClass: dataE2eCardContainerClass }), groupType === 'Children' && checkedInCount ? _jsx(StyledCount, { children: checkedInCount }) : null] }) }) }));
};
export const Card = ({ image, imageSize, displayTitle, initials, color, onClick, selected, dataE2eCardContainerClass, alignment, }) => {
    const hasImage = React.useMemo(() => image && !image.includes(EMPTY_IMAGE_NAME), [image]);
    const p200 = useCustomWhitelabelColor('p200');
    const p300 = useCustomWhitelabelColor('p300');
    const imageElement = React.useMemo(() => {
        if (hasImage) {
            return (_jsx(ImageContainer, { size: imageSize, children: _jsx(StyledImage, { src: image }) }));
        }
        if (!hasImage && initials) {
            return _jsx(ImageContainer, { children: initials });
        }
        if (!hasImage && color) {
            return _jsx(ImageContainer, { color: color });
        }
        return null;
    }, [hasImage, image, imageSize, initials, color]);
    return (_jsxs(StyledItemContainer, { onClick: onClick, selected: selected, "data-e2e-class": dataE2eCardContainerClass, customP200: p200, customP300: p300, alignment: alignment, flexDirection: alignment === 'vertical' ? 'column' : 'row', children: [imageElement, _jsx(StyledTextContainer, { children: _jsx(Text, { variant: "h6", emphasized: true, color: "n400", children: displayTitle }) })] }));
};
/*
|---------------------------------------------------------------------------------
| HELPERS
|---------------------------------------------------------------------------------
*/
// eslint-disable-next-line local-rules/no-enum
var TextColors;
(function (TextColors) {
    TextColors["White"] = "#FFFFFF";
    TextColors["Black"] = "#120A20";
})(TextColors || (TextColors = {}));
const GroupColors = {
    '#120A20': { textColor: TextColors.White },
    '#5C34A3': { textColor: TextColors.White },
    '#4FB97C': { textColor: TextColors.Black },
    '#B9EBD9': { textColor: TextColors.Black },
    '#243CC1': { textColor: TextColors.White },
    '#BD2A1F': { textColor: TextColors.White },
    '#FFB7D5': { textColor: TextColors.Black },
    '#D3D3D3': { textColor: TextColors.Black },
    '#6FC7EC': { textColor: TextColors.Black },
    '#FFCA3C': { textColor: TextColors.Black },
    '#F5BD97': { textColor: TextColors.Black },
};
// If a room doesn't have an image, we use a custom color for the room's background.
// The color is derived from the room's id.
const getGroupColor = (groupId) => {
    const colorsCount = Object.keys(GroupColors).length;
    const colorIndex = groupId.charCodeAt(0) % colorsCount;
    const [backgroundColor] = Object.entries(GroupColors)[colorIndex];
    return backgroundColor;
};
